import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/feature/AppLayout";
import { useAuth } from "@/context/AuthContext";

const API_BASE = "https://guardiancloudcare.com/backend/api";

interface CaseItem {
  case_intake_id: number;
  case_number: string;
  report_uid: string;
  case_status: string;
  priority_level: string;
  incident_type: string;
  urgency: string;
  location: string;
  municipality: string;
  province: string;
  child_full_name: string;
  assigned_social_worker_uid: string | null;
  assigned_social_worker_name: string | null;
  assignment_status: string;
  created_at: string;
}

interface Worker {
  worker_uid: string;
  worker_name: string;
  office_name: string;
  active_cases: number;
  completed_cases: number;
  total_assigned_cases: number;
  workload_level: "Low" | "Moderate" | "High";
  recommendation: string;
}

interface CasesResponse {
  success: boolean;
  office_name?: string;
  data?: CaseItem[];
  message?: string;
  error?: string;
}

interface WorkloadResponse {
  success: boolean;
  office_name?: string;
  total_workers?: number;
  total_active_cases?: number;
  workers?: Worker[];
  message?: string;
  error?: string;
}

interface ReassignResponse {
  success: boolean;
  message?: string;
  error?: string;
  case_number?: string;
  previous_worker_uid?: string | null;
  assigned_social_worker_uid?: string;
  assigned_social_worker_name?: string;
  reason?: string;
  workload?: {
    previous_worker_before: number;
    previous_worker_after: number;
    new_worker_before: number;
    new_worker_after: number;
  };
}

interface AssignmentHistoryItem {
  id: number;
  case_number: string;
  previous_worker_uid: string | null;
  previous_worker_name: string | null;
  new_worker_uid: string;
  new_worker_name: string | null;
  supervisor_uid: string;
  supervisor_name: string | null;
  reassignment_reason: string;
  previous_active_cases_before: number;
  new_worker_active_cases_before: number;
  previous_worker_active_cases_after: number | null;
  new_worker_active_cases_after: number | null;
  created_at: string;
}

interface AssignmentHistoryResponse {
  success: boolean;
  office_name?: string;
  total?: number;
  data?: AssignmentHistoryItem[];
  error?: string;
  message?: string;
}

const CLOSED_STATUSES = [
  "closed",
  "completed",
  "cancelled",
  "archived",
];

function isActiveCase(item: CaseItem) {
  return !CLOSED_STATUSES.includes(
    (item.case_status || "").trim().toLowerCase()
  );
}

function workloadLabel(worker: Worker) {
  if (worker.active_cases >= 6) return "High workload";
  if (worker.active_cases >= 3) return "Moderate workload";
  return "Low workload";
}

function workloadClasses(level: string) {
  if (level === "High") {
    return "bg-red-100 text-red-800";
  }

  if (level === "Moderate") {
    return "bg-amber-100 text-amber-800";
  }

  return "bg-teal-100 text-teal-800";
}

function formatDate(value: string) {
  if (!value) return "Date unavailable";

  const parsed = new Date(value.replace(" ", "T"));

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function SummaryCard({
  label,
  value,
  description,
}: {
  label: string;
  value: number;
  description: string;
}) {
  return (
    <div className="rounded-xl border bg-white p-5 shadow-sm">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">
        {value}
      </p>
      <p className="mt-1 text-xs text-gray-500">
        {description}
      </p>
    </div>
  );
}

export default function SupCaseAssignmentPage() {
  const { user } = useAuth();

  const [cases, setCases] = useState<CaseItem[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [officeName, setOfficeName] = useState("");

  const [history, setHistory] = useState<AssignmentHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historySearch, setHistorySearch] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [selectedCaseNumber, setSelectedCaseNumber] = useState("");
  const [selectedWorkerUid, setSelectedWorkerUid] = useState("");
  const [reason, setReason] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadVersion, setLoadVersion] = useState(0);

  const loadHistory = useCallback(async () => {
    if (!user?.uid) {
      setHistory([]);
      setHistoryError("Supervisor account information is unavailable.");
      return;
    }

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const response = await fetch(
        `${API_BASE}/get_case_assignment_history.php?supervisor_uid=${encodeURIComponent(
          user.uid
        )}`
      );

      const result =
        (await response.json()) as AssignmentHistoryResponse;

      if (!response.ok || !result.success) {
        throw new Error(
          result.error ||
            result.message ||
            "Unable to load reassignment history."
        );
      }

      setHistory(result.data ?? []);
    } catch (err) {
      setHistory([]);
      setHistoryError(
        err instanceof Error
          ? err.message
          : "Unable to load reassignment history."
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [user?.uid]);

  const loadData = useCallback(async () => {
    if (!user?.uid) {
      setError("Supervisor account information is unavailable.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    // Load the history separately so a history API failure
    // does not stop the workload dashboard from loading.
    void loadHistory();

    try {
      const [caseResponse, workloadResponse] = await Promise.all([
        fetch(
          `${API_BASE}/get_supervisor_case_assignments.php?supervisor_uid=${encodeURIComponent(
            user.uid
          )}`
        ),
        fetch(
          `${API_BASE}/get_supervisor_worker_workloads.php?supervisor_uid=${encodeURIComponent(
            user.uid
          )}`
        ),
      ]);

      const caseResult =
        (await caseResponse.json()) as CasesResponse;

      const workloadResult =
        (await workloadResponse.json()) as WorkloadResponse;

      if (!caseResponse.ok || !caseResult.success) {
        throw new Error(
          caseResult.message ||
            caseResult.error ||
            "Unable to load office cases."
        );
      }

      if (!workloadResponse.ok || !workloadResult.success) {
        throw new Error(
          workloadResult.message ||
            workloadResult.error ||
            "Unable to load Social Worker workloads."
        );
      }

      setCases(caseResult.data ?? []);
      setWorkers(workloadResult.workers ?? []);
      setOfficeName(
        workloadResult.office_name ||
          caseResult.office_name ||
          "Supervisor office"
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to load workload information."
      );
    } finally {
      setLoading(false);
    }
  }, [user?.uid, loadHistory]);

  useEffect(() => {
    void loadData();
  }, [loadData, loadVersion]);

  const activeCases = useMemo(
    () => cases.filter(isActiveCase),
    [cases]
  );

  const unassignedCases = useMemo(
    () =>
      activeCases.filter(
        (item) => !item.assigned_social_worker_uid
      ),
    [activeCases]
  );

  const highWorkloadCount = workers.filter(
    (worker) => worker.active_cases >= 6
  ).length;

  const filteredCases = useMemo(() => {
    const term = search.trim().toLowerCase();

    return cases.filter((item) => {
      const matchesSearch =
        !term ||
        [
          item.case_number,
          item.report_uid,
          item.child_full_name,
          item.incident_type,
          item.location,
          item.municipality,
          item.province,
          item.assigned_social_worker_name,
        ].some((value) =>
          String(value ?? "").toLowerCase().includes(term)
        );

      const status = (item.case_status || "")
        .trim()
        .toLowerCase();

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Unassigned"
          ? !item.assigned_social_worker_uid
          : status === statusFilter.toLowerCase());

      return matchesSearch && matchesStatus;
    });
  }, [cases, search, statusFilter]);

  const filteredHistory = useMemo(() => {
    const term = historySearch.trim().toLowerCase();

    if (!term) return history;

    return history.filter((item) =>
      [
        item.case_number,
        item.previous_worker_name,
        item.previous_worker_uid,
        item.new_worker_name,
        item.new_worker_uid,
        item.supervisor_name,
        item.supervisor_uid,
        item.reassignment_reason,
      ].some((value) =>
        String(value ?? "").toLowerCase().includes(term)
      )
    );
  }, [history, historySearch]);

  const selectedCase = cases.find(
    (item) => item.case_number === selectedCaseNumber
  );

  const eligibleWorkers = useMemo(() => {
    return [...workers].sort((a, b) => {
      if (a.worker_uid === selectedCase?.assigned_social_worker_uid) {
        return 1;
      }

      if (b.worker_uid === selectedCase?.assigned_social_worker_uid) {
        return -1;
      }

      return (
        a.active_cases - b.active_cases ||
        a.worker_name.localeCompare(b.worker_name)
      );
    });
  }, [workers, selectedCase]);

  const recommendedWorker = eligibleWorkers.find(
    (worker) =>
      worker.worker_uid !==
      selectedCase?.assigned_social_worker_uid
  );

  const selectedWorker = workers.find(
    (worker) => worker.worker_uid === selectedWorkerUid
  );

  const previousWorker = selectedCase?.assigned_social_worker_uid
    ? workers.find(
        (worker) =>
          worker.worker_uid ===
          selectedCase.assigned_social_worker_uid
      )
    : undefined;

  const beginAssignment = (item: CaseItem) => {
    setSelectedCaseNumber(item.case_number);
    setSelectedWorkerUid("");
    setReason("");
    setSuccessMessage("");
    setError("");
  };

  const closeAssignment = () => {
    setSelectedCaseNumber("");
    setSelectedWorkerUid("");
    setReason("");
    setError("");
  };

  const confirmAssignment = async () => {
    if (!user?.uid || !selectedCase || !selectedWorker) {
      setError("Select a case and an eligible Social Worker.");
      return;
    }

    if (
      selectedWorker.worker_uid ===
      selectedCase.assigned_social_worker_uid
    ) {
      setError("Choose a different Social Worker.");
      return;
    }

    if (reason.trim().length < 10) {
      setError("Enter a reason of at least 10 characters.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await fetch(
        `${API_BASE}/reassign_case.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            case_number: selectedCase.case_number,
            uid: selectedWorker.worker_uid,
            supervisor_uid: user.uid,
            reason: reason.trim(),
          }),
        }
      );

      const result =
        (await response.json()) as ReassignResponse;

      if (!response.ok || !result.success) {
        throw new Error(
          result.error || result.message || "Assignment failed."
        );
      }

      const oldName =
        selectedCase.assigned_social_worker_name || "Unassigned";

      setSuccessMessage(
        `${selectedCase.case_number} was assigned to ${selectedWorker.worker_name}. Previous worker: ${oldName}.`
      );

      closeAssignment();

      // Reload cases, workload numbers, and reassignment history.
      setLoadVersion((version) => version + 1);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to save the assignment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout title="">
      <div className="min-h-screen space-y-6 bg-gray-50 p-5 md:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">
              Supervisor / Workload Management
            </p>

            <h1 className="mt-1 text-2xl font-bold text-gray-900">
              Workload &amp; Resource Management
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              {officeName} · Review capacity and make informed
              case-assignment decisions.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setLoadVersion((version) => version + 1)}
            disabled={loading}
            className="rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
          >
            {loading ? "Refreshing..." : "Refresh data"}
          </button>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        {successMessage && (
          <div
            role="status"
            className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-800"
          >
            {successMessage}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            label="Office Cases"
            value={cases.length}
            description="Cases returned by the office API"
          />

          <SummaryCard
            label="Active Cases"
            value={activeCases.length}
            description="Excludes closed and completed cases"
          />

          <SummaryCard
            label="Unassigned Cases"
            value={unassignedCases.length}
            description="Active cases without a worker"
          />

          <SummaryCard
            label="High Workload"
            value={highWorkloadCount}
            description="Workers with 6 or more active cases"
          />
        </div>

        {/* SOCIAL WORKER CAPACITY */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Social Worker Capacity
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Recommendations use active caseload as an initial
              indicator, not as a substitute for professional judgment.
            </p>
          </div>

          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading worker workloads...
            </p>
          ) : workers.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No approved Social Workers were found for this office.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {workers.map((worker) => {
                const maxCases = Math.max(
                  6,
                  ...workers.map((item) => item.active_cases)
                );

                const barWidth = Math.min(
                  100,
                  (worker.active_cases / maxCases) * 100
                );

                return (
                  <div
                    key={worker.worker_uid}
                    className="rounded-xl border p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {worker.worker_name}
                        </h3>

                        <p className="mt-1 text-xs text-gray-500">
                          {worker.worker_uid}
                        </p>
                      </div>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${workloadClasses(
                          worker.workload_level
                        )}`}
                      >
                        {workloadLabel(worker)}
                      </span>
                    </div>

                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <p className="text-sm text-gray-500">
                          Active caseload
                        </p>

                        <p className="text-3xl font-bold text-gray-900">
                          {worker.active_cases}
                        </p>
                      </div>

                      <div className="text-right text-xs text-gray-500">
                        <p>Completed: {worker.completed_cases}</p>
                        <p>
                          Total assigned: {worker.total_assigned_cases}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={`h-full rounded-full ${
                          worker.workload_level === "High"
                            ? "bg-red-500"
                            : worker.workload_level === "Moderate"
                              ? "bg-amber-500"
                              : "bg-teal-500"
                        }`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>

                    <p className="mt-3 text-sm text-gray-600">
                      {worker.recommendation}
                    </p>

                    <button
                      type="button"
                      onClick={() => {
                        setSelectedWorkerUid(worker.worker_uid);
                        setSuccessMessage("");
                      }}
                      className="mt-3 text-sm font-medium text-teal-700 hover:underline"
                    >
                      Select worker
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {selectedWorker && (
          <section className="rounded-xl border border-teal-200 bg-teal-50 p-4">
            <p className="text-sm font-semibold text-teal-900">
              Selected worker: {selectedWorker.worker_name}
            </p>

            <p className="mt-1 text-sm text-teal-800">
              Current active caseload: {selectedWorker.active_cases}.
              Select a case below to review and confirm an assignment.
            </p>

            <button
              type="button"
              onClick={() => setSelectedWorkerUid("")}
              className="mt-2 text-sm font-medium text-teal-800 underline"
            >
              Clear selection
            </button>
          </section>
        )}

        {/* OFFICE CASES */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Office Cases
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Select a case to assign or reassign it to an approved
              worker in your office.
            </p>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search case, child, incident, location..."
              className="rounded-lg border px-3 py-2 text-sm outline-none focus:border-teal-500 md:col-span-2"
            />

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border px-3 py-2 text-sm outline-none focus:border-teal-500"
            >
              <option value="All">All statuses</option>
              <option value="For Approval">For Approval</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Closed">Closed</option>
              <option value="Unassigned">Unassigned worker</option>
            </select>
          </div>

          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading office cases...
            </p>
          ) : filteredCases.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No cases match your search or filter.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-3">Case / Child</th>
                    <th className="px-3 py-3">Incident</th>
                    <th className="px-3 py-3">Location</th>
                    <th className="px-3 py-3">Priority</th>
                    <th className="px-3 py-3">Assigned Worker</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {filteredCases.map((item) => (
                    <tr
                      key={item.case_intake_id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-3 py-4">
                        <p className="font-semibold text-gray-900">
                          {item.case_number}
                        </p>

                        <p className="mt-1 text-gray-600">
                          {item.child_full_name || "Name unavailable"}
                        </p>

                        <p className="mt-1 text-xs text-gray-400">
                          {item.report_uid}
                        </p>
                      </td>

                      <td className="px-3 py-4">
                        {item.incident_type || "Not specified"}
                      </td>

                      <td className="px-3 py-4">
                        {[
                          item.location,
                          item.municipality,
                          item.province,
                        ]
                          .filter(Boolean)
                          .join(", ") || "Not specified"}
                      </td>

                      <td className="px-3 py-4">
                        {item.priority_level || item.urgency || "Normal"}
                      </td>

                      <td className="px-3 py-4">
                        {item.assigned_social_worker_name || (
                          <span className="text-amber-700">
                            Unassigned
                          </span>
                        )}

                        {item.assigned_social_worker_uid && (
                          <p className="mt-1 text-xs text-gray-500">
                            {item.assigned_social_worker_uid}
                          </p>
                        )}
                      </td>

                      <td className="px-3 py-4">
                        {item.case_status || "Unknown"}
                      </td>

                      <td className="px-3 py-4">
                        <button
                          type="button"
                          onClick={() => {
                            beginAssignment(item);

                            if (selectedWorkerUid) {
                              setSelectedWorkerUid(selectedWorkerUid);
                            }
                          }}
                          className="font-medium text-teal-700 hover:underline"
                        >
                          {item.assigned_social_worker_uid
                            ? "Reassign"
                            : "Assign"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="mt-4 text-xs text-gray-500">
                Showing {filteredCases.length} of {cases.length} cases.
              </p>
            </div>
          )}
        </section>

        {/* ASSIGNMENT MODAL */}
        {selectedCase && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="assignment-title"
              className="my-6 w-full max-w-xl rounded-2xl bg-white shadow-xl"
            >
              <div className="border-b p-5">
                <h2
                  id="assignment-title"
                  className="text-xl font-bold text-gray-900"
                >
                  Review Assignment
                </h2>

                <p className="mt-1 text-sm text-gray-500">
                  The Supervisor makes the final decision.
                </p>
              </div>

              <div className="space-y-5 p-5">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs uppercase text-gray-500">
                    Case reference
                  </p>

                  <p className="mt-1 font-semibold text-gray-900">
                    {selectedCase.case_number}
                  </p>

                  <p className="mt-1 text-sm text-gray-600">
                    {selectedCase.child_full_name}
                  </p>

                  <p className="mt-2 text-sm text-gray-600">
                    Current worker:{" "}
                    {selectedCase.assigned_social_worker_name ||
                      "Unassigned"}
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="worker-select"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Recommended and eligible workers
                  </label>

                  <select
                    id="worker-select"
                    value={selectedWorkerUid}
                    onChange={(event) =>
                      setSelectedWorkerUid(event.target.value)
                    }
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-teal-500 focus:outline-none"
                  >
                    <option value="">Select a Social Worker</option>

                    {eligibleWorkers
                      .filter(
                        (worker) =>
                          worker.worker_uid !==
                          selectedCase.assigned_social_worker_uid
                      )
                      .map((worker) => (
                        <option
                          key={worker.worker_uid}
                          value={worker.worker_uid}
                        >
                          {worker.worker_name} — {worker.active_cases} active
                          case(s) — {worker.workload_level} workload
                          {worker.worker_uid ===
                          recommendedWorker?.worker_uid
                            ? " (Recommended)"
                            : ""}
                        </option>
                      ))}
                  </select>

                  <p className="mt-2 text-xs text-gray-500">
                    Workers are sorted by active caseload. The Supervisor
                    must confirm the final decision.
                  </p>
                </div>

                {selectedWorker && (
                  <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
                    <p className="text-sm font-semibold text-teal-900">
                      Proposed assignment: {selectedWorker.worker_name}
                    </p>

                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-lg bg-white p-3">
                        <p className="text-xs text-gray-500">
                          Previous worker
                        </p>

                        <p className="mt-1 font-medium text-gray-900">
                          {selectedCase.assigned_social_worker_name ||
                            "Unassigned"}
                        </p>

                        <p className="mt-2 text-sm text-gray-700">
                          Before: {previousWorker?.active_cases ?? 0}
                        </p>

                        <p className="text-sm font-semibold text-teal-700">
                          After:{" "}
                          {Math.max(
                            0,
                            (previousWorker?.active_cases ?? 0) -
                              (previousWorker && isActiveCase(selectedCase)
                                ? 1
                                : 0)
                          )}
                        </p>
                      </div>

                      <div className="rounded-lg bg-white p-3">
                        <p className="text-xs text-gray-500">
                          New worker
                        </p>

                        <p className="mt-1 font-medium text-gray-900">
                          {selectedWorker.worker_name}
                        </p>

                        <p className="mt-2 text-sm text-gray-700">
                          Before: {selectedWorker.active_cases}
                        </p>

                        <p className="text-sm font-semibold text-teal-700">
                          After:{" "}
                          {selectedWorker.active_cases +
                            (isActiveCase(selectedCase) ? 1 : 0)}
                        </p>
                      </div>
                    </div>

                    <p className="mt-3 text-xs text-teal-800">
                      These are estimates. The saved workload counts from
                      the server are authoritative.
                    </p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="assignment-reason"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Assignment or reassignment reason *
                  </label>

                  <textarea
                    id="assignment-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={4}
                    maxLength={2000}
                    placeholder="Explain the workload, availability, urgency, or other reason for this decision..."
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-teal-500 focus:outline-none"
                  />

                  <p className="mt-1 text-xs text-gray-500">
                    At least 10 characters. {reason.trim().length}/2000
                  </p>
                </div>

                {error && (
                  <p role="alert" className="text-sm text-red-700">
                    {error}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-3 border-t p-5">
                <button
                  type="button"
                  onClick={closeAssignment}
                  disabled={submitting}
                  className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => void confirmAssignment()}
                  disabled={
                    submitting ||
                    !selectedWorkerUid ||
                    reason.trim().length < 10
                  }
                  className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Confirm Assignment"}
                </button>
              </div>
            </section>
          </div>
        )}

        {/* AUDIT TRAIL NOTICE */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <h3 className="font-semibold text-blue-900">
            Supervisor decision and audit trail
          </h3>

          <p className="mt-1 text-sm text-blue-800">
            Recommendations are based on current active caseload. They do
            not automatically assign cases. Confirmed changes must be
            recorded with a reason and the resulting workload counts.
          </p>
        </div>

        {/* REASSIGNMENT HISTORY */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Reassignment History
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Audit trail of previous case transfers, reasons, and
                workload changes recorded for your office.
              </p>
            </div>

            <button
              type="button"
              onClick={() => void loadHistory()}
              disabled={historyLoading}
              className="rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
            >
              {historyLoading ? "Loading..." : "Refresh history"}
            </button>
          </div>

          <input
            value={historySearch}
            onChange={(event) => setHistorySearch(event.target.value)}
            placeholder="Search case, workers, supervisor, or reason..."
            className="mb-4 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-teal-500"
          />

          {historyError && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {historyError}
              <p className="mt-1 text-xs">
                Check that get_case_assignment_history.php returns valid
                JSON with a data array.
              </p>
            </div>
          )}

          {historyLoading && history.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading reassignment history...
            </p>
          ) : filteredHistory.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <h3 className="font-medium text-gray-800">
                {history.length === 0
                  ? "No reassignment history found"
                  : "No matching history records"}
              </h3>

              <p className="mt-2 text-sm text-gray-500">
                {history.length === 0
                  ? "Recorded transfers will appear here after a case is reassigned."
                  : "Try a different search term."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredHistory.map((item) => (
                <article
                  key={item.id}
                  className="rounded-xl border p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        Case number
                      </p>

                      <h3 className="mt-1 font-semibold text-gray-900">
                        {item.case_number}
                      </h3>
                    </div>

                    <p className="text-xs text-gray-500">
                      {formatDate(item.created_at)}
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Previous Social Worker
                      </p>

                      <p className="mt-1 font-medium text-gray-900">
                        {item.previous_worker_name || "Unassigned"}
                      </p>

                      <p className="mt-1 text-xs text-gray-500">
                        {item.previous_worker_uid || "No previous worker"}
                      </p>

                      <p className="mt-3 text-sm text-gray-600">
                        Active cases before:{" "}
                        {item.previous_active_cases_before}
                      </p>

                      <p className="text-sm text-gray-600">
                        Active cases after:{" "}
                        {item.previous_worker_active_cases_after ?? "N/A"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-teal-50 p-3">
                      <p className="text-xs font-semibold uppercase text-teal-700">
                        New Social Worker
                      </p>

                      <p className="mt-1 font-medium text-gray-900">
                        {item.new_worker_name || item.new_worker_uid}
                      </p>

                      <p className="mt-1 text-xs text-gray-500">
                        {item.new_worker_uid}
                      </p>

                      <p className="mt-3 text-sm text-gray-600">
                        Active cases before:{" "}
                        {item.new_worker_active_cases_before}
                      </p>

                      <p className="text-sm text-gray-600">
                        Active cases after:{" "}
                        {item.new_worker_active_cases_after ?? "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border p-3">
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Reassignment reason
                    </p>

                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                      {item.reassignment_reason || "No reason recorded"}
                    </p>
                  </div>

                  <div className="mt-3 text-sm text-gray-600">
                    <span className="font-medium">Supervisor:</span>{" "}
                    {item.supervisor_name || item.supervisor_uid}
                  </div>
                </article>
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-gray-500">
            Displaying {filteredHistory.length} of {history.length} history
            record(s).
          </p>
        </section>
      </div>
    </AppLayout>
  );
}